// app/ningenie/components/AuthProvider.tsx
// baby: Auth provider wrapper for ningenie routes - shows app with mock data when not authenticated

'use client'

import { useState, useEffect, useRef } from 'react'
import { auth, onAuthStateChanged } from '@/app/lib/firebase'
import type { User } from 'firebase/auth'

type AuthProviderProps = {
  children: React.ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const prevUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      const currentUserId = currentUser?.uid || null
      const prevUserId = prevUserIdRef.current

      // baby: Centralized login bootstrap for the whole web app.
      // baby: Only run when auth identity actually changes, not on every render.
      if (prevUserId !== currentUserId) {
        prevUserIdRef.current = currentUserId

        if (currentUser) {
          currentUser.getIdToken().then((token) => {
            fetch('/api/ningenie/ensure-user', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            }).catch(() => {})
          }).catch(() => {})
        }
      }

      setUser(currentUser)
      setIsLoading(false)
    })

    return () => unsubscribe()
  }, [])

  // baby: show loading while checking auth state
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  // baby: always show children - app will show mock data when not authenticated
  return <>{children}</>
}

// baby: export user state hook for components
export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser)
      setIsLoading(false)
    })

    return () => unsubscribe()
  }, [])

  return { user, isLoading, isAuthenticated: !!user }
}

